import { construirLoteDatasAvulsas } from './escalaDatasAvulsas';

const codigo = (valor) => String(valor || '').trim().toUpperCase();
const idPersistido = (valor) => /^\d+$/.test(String(valor || '')) && Number(valor) > 0 ? Number(valor) : null;
const erro = (codigoErro, mensagem) => {
	const exception = new Error(mensagem);
	exception.codigo = codigoErro;
	throw exception;
};
const copiar = (valor) => JSON.parse(JSON.stringify(valor));

const dataIso = (valor) => {
	let texto = String(valor || '').trim();
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) texto = texto.split('/').reverse().join('-');
	if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return '';
	const data = new Date(`${texto}T00:00:00Z`);
	return Number.isFinite(data.getTime()) && data.toISOString().slice(0, 10) === texto ? texto : '';
};
const dataExibida = (valor) => dataIso(valor).split('-').reverse().join('/');
const horaApi = (valor) => {
	if (valor == null || valor === '') return null;
	let hora = String(valor).trim();
	if (/^\d{1,2}$/.test(hora)) hora = `${hora.padStart(2, '0')}:00:00`;
	else if (/^\d{2}:\d{2}$/.test(hora)) hora += ':00';
	return /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(hora) ? hora : '';
};
const horarioCard = (card, celula) => {
	const horario = card.edicao?.horario || card.horario || card.dados_edicao?.horario || card.dados_edicao || card;
	return {
		data: dataIso(horario.data || celula.data),
		hora_inicio: horaApi(horario.hora_inicio),
		hora_fim: horaApi(horario.hora_fim),
	};
};

export const ehLinhaManualSemRegime = (linha = {}) =>
	codigo(linha.origem_linha) === 'MANUAL' &&
	!idPersistido(linha.regime_vinculo_id || linha.regime_escala?.id);

export const construirItemGerenciamentoManual = ({ linha, setorOptions = [], contextoAplicado = {} } = {}) => {
	if (!ehLinhaManualSemRegime(linha)) return null;
	if (!Array.isArray(linha.celulas)) return null;
	const setorId = String(linha.setor?.id || linha.setor_id || linha.escala_mensal?.setor_id || '');
	const setor = setorOptions.find((item) => String(item.id || item.setor_id) === setorId);
	const competencia = String(contextoAplicado.mes_referencia || linha.escala_mensal?.mes_referencia || '').slice(0, 7);
	const vistos = new Set();
	const datasEspecificas = linha.celulas.flatMap((celula = {}, indiceCelula) => (celula.cards || []).flatMap((card = {}, indiceCard) => {
		const itemId = idPersistido(card.id);
		const horarioOriginal = horarioCard(card, celula);
		if (competencia && horarioOriginal.data.slice(0, 7) !== competencia) return [];
		if (itemId && vistos.has(itemId)) return [];
		if (itemId) vistos.add(itemId);
		const derivado = Boolean(card.jornada_id || card.jornada_ocorrencia_id || card.edicao?.jornada_id);
		const sintetico = !itemId || card.sintetico === true || card.persistido === false;
		const capacidades = {
			pode_editar: !sintetico && !derivado && card.acoes?.pode_editar === true,
			pode_excluir: !sintetico && !derivado && card.acoes?.pode_excluir === true,
			pode_trocar: !sintetico && card.acoes?.pode_trocar === true,
		};
		const manualKey = itemId ? `manual-card-${itemId}` : `manual-sinal-${indiceCelula}-${indiceCard}`;
		return [{
			id: manualKey,
			manualKey,
			itemId,
			data: horarioOriginal.data,
			dataLabel: dataExibida(horarioOriginal.data),
			horaInicio: (horarioOriginal.hora_inicio || '').slice(0, 5),
			horaFim: (horarioOriginal.hora_fim || '').slice(0, 5),
			horarioOriginal,
			capacidades,
			sourceCard: card,
			observacao: card.observacao || '',
			sintetico,
			readOnly: !capacidades.pode_editar,
			legado: false,
			origem: codigo(card.tipo_item) === 'TROCA' ? 'troca' : 'manual',
			label: card.rotulo_tipo || card.tipo_item || '',
		}];
	}));
	if (!datasEspecificas.length) return null;
	return {
		id: `${setorId}-${linha.linha_id || 'manual'}`,
		manual: true,
		sourceLinha: linha,
		setorId,
		setorNome: linha.setor?.nome || setor?.nome || setor?.label || '',
		regimeVinculoId: null,
		regime: linha.escala_mensal?.regime || linha.regime_escala?.regime || '',
		tipoRecorrencia: 'DATAS_ESPECIFICAS',
		nomeRecorrencia: 'Datas específicas',
		recorrenciaValue: 'Datas específicas',
		dataInicial: dataExibida(datasEspecificas[0].data),
		dataUltimaAtualizacao: linha.ultima_atualizacao || '',
		diasSemanaValores: [],
		diasSemana: '',
		horariosByDia: {},
		horariosOptionsByDia: {},
		horariosParticionadosByDia: {},
		horaInicio: '',
		horaFim: '',
		horario: '',
		datasEspecificas,
	};
};

const horarioEditado = (dataItem, original) => {
	const data = dataIso(dataItem.data);
	if (!data) erro('DATA_MANUAL_INVALIDA', 'Preencha uma data válida; remova a linha para excluir o card.');
	const resultado = { data };
	[['horaInicio', 'hora_inicio'], ['horaFim', 'hora_fim']].forEach(([campoUi, campoApi]) => {
		const informado = dataItem[campoUi];
		const anterior = original?.horarioOriginal?.[campoApi];
		// O controle HH:mm não representa os segundos. Enquanto o texto visível
		// não mudar, a precisão do registro persistido deve ser preservada.
		if (original && String(informado || '') === String(original[campoUi] || '')) {
			resultado[campoApi] = anterior;
		} else {
			resultado[campoApi] = horaApi(informado);
			if (!resultado[campoApi]) erro('HORARIO_MANUAL_INVALIDO', 'Preencha horários válidos para todas as datas alteradas.');
		}
	});
	return resultado;
};

const validarPayloadCriacao = (payload) => {
	if (!payload || (!payload.escala_mensal_id && !payload.contexto_competencia)) {
		erro('CONTEXTO_CRIACAO_MANUAL_AUSENTE', 'Carregue o formulário do setor antes de adicionar datas.');
	}
	if (codigo(payload.tipo_item) === 'VACANCIA') {
		if (!idPersistido(payload.vinculo_tipo_id)) erro('VINCULO_VACANCIA_AUSENTE', 'Informe o tipo de vínculo da vacância antes de adicionar datas.');
	} else if (!((idPersistido(payload.profissional?.id) && idPersistido(payload.profissional?.vinculo_id)) || payload.profissional?.cpf || payload.profissional_por_cpf?.cpf)) {
		erro('CONTRATO_CRIACAO_MANUAL_AUSENTE', 'Selecione um contrato ou resolva o profissional no formulário antes de adicionar datas.');
	}
};

// Produz um plano fechado. PATCH/DELETE são comandos independentes: o executor
// deve registrar confirmações e reler o backend, sem anunciar atomicidade total.
export const planejarAlteracoesManuais = ({ itemOriginal, itemEditado, payloadCriacao } = {}) => {
	if (!itemOriginal?.manual || !ehLinhaManualSemRegime(itemOriginal.sourceLinha)) {
		erro('ALVO_MANUAL_INVALIDO', 'A edição manual exige uma linha manual detalhada.');
	}
	if (String(itemEditado?.setorId || '') !== String(itemOriginal.setorId || '')) {
		erro('CONTEXTO_MANUAL_ALTERADO', 'A seleção manual deve permanecer no setor consultado.');
	}
	if (!Array.isArray(itemEditado?.datasEspecificasEditaveis)) {
		erro('SELECAO_MANUAL_AUSENTE', 'Não foi possível identificar a seleção explícita de cards.');
	}
	const originais = itemOriginal.datasEspecificas || [];
	const porId = new Map(originais.filter((item) => item.itemId).map((item) => [item.itemId, item]));
	const porChave = new Map(originais.map((item) => [item.manualKey, item]));
	const encontrados = new Set();
	const patches = [];
	const novas = [];
	itemEditado.datasEspecificasEditaveis.forEach((dataItem) => {
		const itemId = idPersistido(dataItem.itemId);
		const original = itemId ? porId.get(itemId) : porChave.get(dataItem.manualKey);
		if (!original && (dataItem.itemId != null || dataItem.manualKey || dataItem.sourceCard)) {
			erro('CARD_MANUAL_NAO_PERTENCE_SELECAO', 'Um card não pertence à seleção consultada; recarregue a linha.');
		}
		if (!original) {
			if (dataItem.sintetico || dataItem.readOnly || dataItem.capacidades) erro('SINALIZACAO_MANUAL_IMUTAVEL', 'Sinalizações de ausência não podem ser criadas ou alteradas como cards.');
			novas.push(horarioEditado(dataItem));
			return;
		}
		if (encontrados.has(original.manualKey)) erro('CARD_MANUAL_DUPLICADO', 'O mesmo card foi selecionado mais de uma vez.');
		encontrados.add(original.manualKey);
		const horario = horarioEditado(dataItem, original);
		const patchHorario = Object.fromEntries(Object.entries(horario).filter(([campo, valor]) => valor !== original.horarioOriginal[campo]));
		const body = {};
		if (Object.keys(patchHorario).length) body.horario = patchHorario;
		if (dataItem.observacao !== undefined && dataItem.observacao !== original.observacao) body.observacao = dataItem.observacao;
		if (!Object.keys(body).length) return;
		if (!original.itemId || !original.capacidades.pode_editar) erro('CARD_MANUAL_SEM_EDICAO', 'O backend não permite editar um dos cards selecionados.');
		patches.push({ itemId: original.itemId, body });
	});
	const exclusoes = originais.filter((item) => !encontrados.has(item.manualKey)).map((item) => {
		if (!item.itemId || !item.capacidades.pode_excluir) erro('CARD_MANUAL_SEM_EXCLUSAO', 'O backend não permite excluir um dos cards selecionados.');
		return { itemId: item.itemId };
	});
	let criacao = null;
	if (novas.length) {
		validarPayloadCriacao(payloadCriacao);
		criacao = construirLoteDatasAvulsas(payloadCriacao, novas);
	}
	return copiar({ patches, exclusoes, criacao, temAlteracoes: Boolean(patches.length || exclusoes.length || criacao), atomico: false });
};
